import React from 'react';
import { Theme } from '../Theme';

type Props = {
    points: Array<{
        date: string;
        score: number;
    }>;
    activePointIndex?: number | null;
    onHoverPoint?: (pointIndex: number) => void;
};

function formatDateLabel(date: Date): string {
    return date.toLocaleDateString(undefined, {
        day: '2-digit',
        month: 'short',
    }).toUpperCase();
}

export function AssessmentScoreChart({ points, activePointIndex = null, onHoverPoint }: Props) {
    const width = 720;
    const height = 180;
    const padding = { top: 16, right: 18, bottom: 36, left: 48 };
    const chartWidth = width - padding.left - padding.right;
    const chartHeight = height - padding.top - padding.bottom;

    const scores = points.map((point) => point.score);
    const rawMin = Math.min(...scores);
    const rawMax = Math.max(...scores);
    const minValue = rawMin === rawMax ? Math.min(0, rawMin - 1) : Math.floor(rawMin);
    const maxValue = rawMin === rawMax ? Math.max(rawMax + 1, 1) : Math.ceil(rawMax);
    const valueRange = Math.max(maxValue - minValue, 1);
    const step = points.length > 1 ? chartWidth / (points.length - 1) : 0;

    const chartPoints = points.map((point, index) => {
        const x = padding.left + (step * index);
        const y = padding.top + chartHeight - (((point.score - minValue) / valueRange) * chartHeight);
        return { x, y, date: point.date, score: point.score };
    });

    const polylinePoints = chartPoints.map((point) => `${point.x},${point.y}`).join(' ');
    const yTicks = [maxValue, minValue + valueRange / 2, minValue];
    const xTickIndexes = Array.from(new Set([
        0,
        Math.floor((points.length - 1) / 2),
        points.length - 1,
    ]));

    return (
        <svg
            viewBox={`0 0 ${width} ${height}`}
            style={styles.chart}
            role='img'
            aria-label='Assessment score chart'
        >
            {yTicks.map((tickValue) => {
                const y = padding.top + chartHeight - (((tickValue - minValue) / valueRange) * chartHeight);

                return (
                    <g key={tickValue}>
                        <text x={padding.left - 10} y={y + 4} textAnchor='end' style={styles.yAxisLabel}>
                            {Number.isInteger(tickValue) ? tickValue : tickValue.toFixed(1)}
                        </text>
                        <line
                            x1={padding.left}
                            y1={y}
                            x2={width - padding.right}
                            y2={y}
                            style={styles.gridLine}
                        />
                    </g>
                );
            })}

            {xTickIndexes.map((tickIndex) => {
                const point = chartPoints[tickIndex];
                if (!point) {
                    return null;
                }

                return (
                    <text
                        key={tickIndex}
                        x={point.x}
                        y={height - 10}
                        textAnchor={tickIndex === 0 ? 'start' : tickIndex === points.length - 1 ? 'end' : 'middle'}
                        style={styles.xAxisLabel}
                    >
                        {formatDateLabel(new Date(point.date))}
                    </text>
                );
            })}

            <polyline
                points={polylinePoints}
                fill='none'
                stroke='#7B7AE6'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
            />

            {chartPoints.map((point, pointIndex) => (
                <g key={`${point.date}-${point.score}`}>
                    <circle
                        cx={point.x}
                        cy={point.y}
                        r={activePointIndex === pointIndex ? '5' : '3'}
                        fill='#7B7AE6'
                        stroke={activePointIndex === pointIndex ? '#ffffff' : 'none'}
                        strokeWidth={activePointIndex === pointIndex ? '2' : '0'}
                    />
                    <circle
                        cx={point.x}
                        cy={point.y}
                        r='20'
                        fill='transparent'
                        style={styles.hoverTarget}
                        onMouseEnter={() => onHoverPoint?.(pointIndex)}
                    />
                </g>
            ))}
        </svg>
    );
}

const styles: Record<string, React.CSSProperties> = {
    chart: {
        width: '720px',
        maxWidth: '100%',
        height: '180px',
        display: 'block',
        overflow: 'visible',
    },
    gridLine: {
        stroke: 'rgba(0, 0, 0, 0.12)',
        strokeWidth: 1,
        strokeDasharray: '6 8',
    },
    yAxisLabel: {
        ...Theme.Fonts.body,
        fontSize: '11px',
        fill: 'rgba(0, 0, 0, 0.45)',
    },
    xAxisLabel: {
        ...Theme.Fonts.body,
        fontSize: '11px',
        fill: 'rgba(0, 0, 0, 0.45)',
    },
    hoverTarget: {
        cursor: 'pointer',
    },
};
